import { callFormSections } from "@/lib/call-form";
import { detailEditable, editable, ownFields } from "@/lib/record-fields";
import { type ContractAsk, type ContractProblem, askedMessage, contractAsk } from "@/lib/send-contract";
import type { ContractCheck, ContractField, RecordField, RecordSection, Role } from "@/lib/types";

/**
 * Send contract on the Phone page (ACL-560): the rep sends the lead their agreement from the call's card,
 * and what the dialog lists to fix is filled in on the call's form, without leaving the dialer.
 *
 * Every decision is here, pure, for `test/call-contract.test.ts`: when the card offers the button, what
 * closing the dialog asks of the form, and where on the form each value it names is filled in. The
 * button and the dialog are `softphone/call-contract.tsx`; the form's half is `softphone/call-form.tsx`.
 * The rest is the record's Send contract (`lib/send-contract.ts`, ACL-541), word for word.
 */

/**
 * Whether the call's card offers Send contract: on the call, and after it, while the outcome window is
 * open and while the page keeps the lead for their form to be finished, so a contract agreed at the end
 * of the call can still go out. Not for a lead the dialer only offers or is still dialing: nobody has
 * spoken to them yet. A call that rings has no card under it to offer it on.
 */
export function contractOffered(state: {
  /** The rep is on a call (connected, not ringing). */
  onCall: boolean;
  /** The ACD's outcome window is open. */
  inWindow: boolean;
  /** A call, a dial or the window still names the page's lead; false once it is only kept. */
  live: boolean;
}): boolean {
  return state.onCall || state.inWindow || !state.live;
}

/**
 * The key the call's ask names the lead's firm by. The firm is no field of the form: it is the
 * contact's own (`PATCH /leads/{id}` with `firm_id`), and core-api checks it before any value, so a
 * lead without one is told only that (`no_firm`), and the values come at the next press.
 */
export const FIRM_KEY = "firm_id";

/** Said under the firm's picker while the lead has none. */
export const FIRM_PROBLEM = "Choose the firm: it decides which agreement goes out.";

/**
 * What closing the dialog on the call asks the form to fix, or null for nothing: the values the record's
 * Send contract would mark (`contractAsk` of `contractToFix`), else the firm when the check says the
 * lead has none. `textOf` reads a field's text now, which a wrong value is marked against.
 */
export function callContractAsk(
  found: { code: string | null; fields: ContractField[]; detail: string | null } | null,
  check: ContractCheck | undefined,
  textOf: (key: string) => string | null,
): ContractAsk | null {
  if (found) return contractAsk(found, textOf);
  if (check && !check.can_send && check.reason === "no_firm") {
    return new Map([[FIRM_KEY, { message: FIRM_PROBLEM, was: null }]]);
  }
  return null;
}

/** The toast when Send contract stops at the form: the record's (`askedMessage`), or the firm. */
export function callAskedMessage(
  found: { code: string | null; fields: ContractField[]; detail: string | null } | null,
): { title: string; detail: string } {
  if (!found) return { title: "Choose the firm", detail: "Needed to send the contract: Firm." };
  return askedMessage(found.fields, found.code, found.detail);
}

/** The folder the call's form draws first while an ask names values it has no input for. */
export const NEEDED_SECTION = "needed_for_the_contract";
export const NEEDED_TITLE = "Needed for the contract";

/**
 * One value the ask names that the call's folders have no input for, and how the folder fills it in:
 *  - `answer`: one of the form's own fields in a folder the call leaves out (a minor's guardian), saved
 *    as the call's other answers are (`PATCH /leads/{id}/fields`);
 *  - `detail`: one of the contact's own details this role may change (the e-mail, the name), saved
 *    through the contact (`PATCH /leads/{id}`), as the record's details panel saves it;
 *  - `firm`: the lead has no firm, picked as on the record;
 *  - `read`: a field this role may only read, said with why it is needed;
 *  - `elsewhere`: a value the form this role sees does not hold at all, said with why it is needed.
 */
export type NeededRow =
  | { kind: "answer" | "detail" | "read"; key: string; field: RecordField; problem: ContractProblem }
  | { kind: "firm" | "elsewhere"; key: string; problem: ContractProblem };

/**
 * Where the call's form takes each value an ask names. A field the call's folders draw with an input is
 * marked where it stands, and is not here; every other one is a row of "Needed for the contract", in the
 * order the dialog listed them. `sections` is the whole form as `GET /leads/{id}/fields` sends it, the
 * platform's own fields and the folders the call leaves out included; until it is read, nothing is said
 * to be missing from it.
 */
export function neededFields(
  ask: ContractAsk | null,
  sections: readonly RecordSection[] | undefined,
  role: Role | undefined,
): NeededRow[] {
  if (!ask || ask.size === 0 || sections === undefined) return [];
  const inPlace = new Set(callFormSections(sections).flatMap((s) => ownFields(s).filter(editable).map((f) => f.key)));
  const byKey = new Map(sections.flatMap((s) => s.fields.map((f) => [f.key, f] as const)));
  const rows: NeededRow[] = [];
  for (const [key, problem] of ask) {
    if (inPlace.has(key)) continue;
    const field = byKey.get(key);
    if (field === undefined) {
      rows.push({ kind: key === FIRM_KEY ? "firm" : "elsewhere", key, problem });
    } else if (field.system) {
      rows.push({ kind: detailEditable(field, role) ? "detail" : "read", key, field, problem });
    } else {
      rows.push({ kind: editable(field) ? "answer" : "read", key, field, problem });
    }
  }
  return rows;
}
